/**
 * Utility class in order to manage url component.
 */
export class Url {
  /**
   * Replace list of query string parameters.
   *
   * @param {string} url - String which contains the query string parameters to replace.
   * @param {object} params - List of query string parameter to add in url.
   * @returns {string} The url with the replaced parameters value.
   * @throws {TypeError} If incoming 'params' parameter is not from object type.
   */
  static replaceParameters(url, params) {
    if (typeof params !== 'object') {
      throw new TypeError('params must be an object');
    }

    let updatedUrl = url;

    Object.keys(params).forEach((name) => {
      updatedUrl = Url.replaceParameter(updatedUrl, name, params[name]);
    });

    return updatedUrl;
  }

  /**
   * Replace a query string parameter.
   *
   * @param {string} url - String which contains the query string parameter to replace.
   * @param {string} name - Parameter name.
   * @param {string} value - Parameter value.
   * @returns {string} The url with the replaced parameter value.
   */
  static replaceParameter(url, name, value) {
    const valueStr = value === undefined ? '' : value;
    const urlParamValue = Url.getParameterValue(url, name);

    // parameter does not exist
    if (urlParamValue === undefined) {
      return Url.addParameter(url, name, valueStr);
    }

    // existing value is different than the new value
    if (decodeURIComponent(urlParamValue) !== valueStr) {
      const regex = new RegExp(`([?&]${encodeURIComponent(name)}=)([^&#]*)`);

      return url.replace(regex, `$1${encodeURIComponent(valueStr)}`);
    }

    // else parameter values are equals
    return url;
  }

  /**
   * Get parameter value in url.
   *
   * @param {string} url - String which could contains the parameter.
   * @param {string} name - Parameter name.
   * @returns {string} Parameter value or undefined if parameter does not exists.
   */
  static getParameterValue(url, name) {
    const regex = new RegExp(`([?&]${encodeURIComponent(name)}=)([^&#]*)`);
    const match = url.match(regex);

    return match !== null ? match[2] : undefined;
  }

  /**
   * Add a query string parameter at the end of the url.
   *
   * @param {string} url - String which contains the query string parameter to replace.
   * @param {string} name - Parameter name.
   * @param {string} value - Parameter value.
   * @returns {string} Url with the new parameter.
   */
  static addParameter(url, name, value) {
    const val = value === undefined ? '' : value;

    const hash = Url.getHash(url);
    const urlNoHash = hash ? `${url.substr(0, url.length - hash.length)}` : url;

    const qsSeparator = Url.hasAtLeastOneParameter(urlNoHash) ? '&' : '?';
    const qsPart = `${qsSeparator}${encodeURIComponent(name)}=${encodeURIComponent(val)}`;

    if (hash) {
      return `${urlNoHash}${qsPart}${hash}`;
    }

    return `${url}${qsPart}`;
  }

  /**
   * Remove a query string parameter at the end of the url.
   *
   * @param {string} url - The url to parse.
   * @param {string} parameter - The parameter to delete.
   */
  static removeParameter(url, parameter) {
    return url.replace(new RegExp(`[?&]${parameter}=[^&#]*(#.*)?$`), '$1').replace(new RegExp(`([?&])${parameter}=[^&]*&`), '$1');
  }

  /**
   * Check if url contains at least one parameter.
   *
   * @param {string} url - String which could contains parameter.
   * @returns {boolean} Parameter value or undefined if parameter does not exists.
   */
  static hasAtLeastOneParameter(url) {
    return url.match(/\?[^=]+/) !== null;
  }

  /**
   * Get url hash.
   *
   * @param {string} url - String which could contains hash.
   * @returns {string} Hash value or undefined if hash does not exists.
   */
  static getHash(url) {
    const match = url.match(/(#.*)$/);

    return match !== null ? match[1] : undefined;
  }
}
